import { useState, useEffect, useCallback } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import type { JourneyItemTemplateBaseFirestore, JourneyProgressType } from '../../services/journeyTypes';

export interface CommonJourneyItemFormData {
    title: string;
    description: string;
    ollieText: string;
    textTemplatingPrompt: string;
    brainPoints: number;
    energyCost: number;
    progressType: JourneyProgressType;
    maxProgress?: number;
}

interface UseJourneyItemFormOptions<T extends JourneyItemTemplateBaseFirestore> {
    itemTemplate?: T;
    initialOrder: number;
    defaultProgressType?: JourneyProgressType;
}

interface UseJourneyItemFormReturn<T extends JourneyItemTemplateBaseFirestore> {
    commonFormData: CommonJourneyItemFormData;
    loading: boolean;
    error: string | null;
    handleCommonChange: (field: keyof CommonJourneyItemFormData, value: string | number | boolean) => void;
    setError: (error: string | null) => void;
    setLoading: (loading: boolean) => void;
    validateCommonFields: () => string | null;
    createBaseJourneyItem: () => Partial<T>;
    handleSubmit: (
        specificValidation: () => string | null,
        createItem: (baseData: Partial<T>) => T,
        onSave: (item: T) => void | Promise<void>,
    ) => (e: React.FormEvent) => Promise<void>;
    handleSaveAndContinue: (
        specificValidation: () => string | null,
        createItem: (baseData: Partial<T>) => T,
        onSaveAndContinue: (item: T) => void | Promise<void>,
    ) => () => Promise<void>;
}

export const useJourneyItemForm = <T extends JourneyItemTemplateBaseFirestore>({
    itemTemplate,
    initialOrder,
    defaultProgressType = 'fixed',
}: UseJourneyItemFormOptions<T>): UseJourneyItemFormReturn<T> => {
    const [commonFormData, setCommonFormData] = useState<CommonJourneyItemFormData>({
        title: '',
        description: '',
        ollieText: '',
        textTemplatingPrompt: '',
        brainPoints: 10,
        energyCost: 1,
        progressType: defaultProgressType,
        maxProgress: 1,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (itemTemplate && itemTemplate.id) {
            setCommonFormData({
                title: itemTemplate.title,
                description: itemTemplate.description,
                ollieText: itemTemplate.ollieText,
                textTemplatingPrompt: itemTemplate.textTemplatingPrompt,
                brainPoints: itemTemplate.brainPoints,
                energyCost: itemTemplate.energyCost,
                progressType: itemTemplate.progressType,
                maxProgress: itemTemplate.maxProgress,
            });
        }
    }, [itemTemplate]);

    const handleCommonChange = useCallback((field: keyof CommonJourneyItemFormData, value: string | number | boolean) => {
        setCommonFormData((prev) => ({ ...prev, [field]: value }));
    }, []);

    const validateCommonFields = useCallback((): string | null => {
        if (!commonFormData.title.trim()) {
            return 'Title is required';
        }
        if (!commonFormData.description.trim()) {
            return 'Description is required';
        }
        if (commonFormData.brainPoints < 0) {
            return 'Brain points must be 0 or greater';
        }
        if (commonFormData.energyCost < 0) {
            return 'Energy cost must be 0 or greater';
        }
        return null;
    }, [commonFormData]);

    const createBaseJourneyItem = useCallback((): Partial<T> => {
        return {
            id: itemTemplate?.id,
            order: initialOrder,
            title: commonFormData.title,
            description: commonFormData.description,
            ollieText: commonFormData.ollieText,
            textTemplatingPrompt: commonFormData.textTemplatingPrompt,
            brainPoints: commonFormData.brainPoints,
            energyCost: commonFormData.energyCost,
            progressType: commonFormData.progressType,
            maxProgress: commonFormData.maxProgress,
            createdAt: itemTemplate?.createdAt || serverTimestamp(),
            updatedAt: serverTimestamp(),
        } as Partial<T>;
    }, [itemTemplate, initialOrder, commonFormData]);

    const handleSubmit = useCallback(
        (
            specificValidation: () => string | null,
            createItem: (baseData: Partial<T>) => T,
            onSave: (item: T) => void | Promise<void>,
        ) => {
            return async (e: React.FormEvent) => {
                e.preventDefault();
                setLoading(true);
                setError(null);

                const commonValidationError = validateCommonFields();
                if (commonValidationError) {
                    setError(commonValidationError);
                    setLoading(false);
                    return;
                }

                const specificValidationError = specificValidation();
                if (specificValidationError) {
                    setError(specificValidationError);
                    setLoading(false);
                    return;
                }

                try {
                    const baseData = createBaseJourneyItem();
                    const itemData = createItem(baseData);
                    const result = onSave(itemData);
                    if (result && typeof result.then === 'function') {
                        await result;
                    }
                } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to save journey item');
                } finally {
                    setLoading(false);
                }
            };
        },
        [validateCommonFields, createBaseJourneyItem],
    );

    const handleSaveAndContinue = useCallback(
        (
            specificValidation: () => string | null,
            createItem: (baseData: Partial<T>) => T,
            onSaveAndContinue: (item: T) => void | Promise<void>,
        ) => {
            return async () => {
                setLoading(true);
                setError(null);

                const commonValidationError = validateCommonFields();
                if (commonValidationError) {
                    setError(commonValidationError);
                    setLoading(false);
                    return;
                }

                const specificValidationError = specificValidation();
                if (specificValidationError) {
                    setError(specificValidationError);
                    setLoading(false);
                    return;
                }

                try {
                    const baseData = createBaseJourneyItem();
                    const itemData = createItem(baseData);
                    const result = onSaveAndContinue(itemData);
                    if (result && typeof result.then === 'function') {
                        await result;
                    }
                } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to save journey item');
                } finally {
                    setLoading(false);
                }
            };
        },
        [validateCommonFields, createBaseJourneyItem],
    );

    return {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        setError,
        setLoading,
        validateCommonFields,
        createBaseJourneyItem,
        handleSubmit,
        handleSaveAndContinue,
    };
};
